import { useMutation } from "@apollo/client/react";
import { gql } from "graphql-tag";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons/faTriangleExclamation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useCallback, useState } from "react";

import { ClientFacingErrors } from "@portal/app/_common/types/clientErrors";
import { LoadingIndicator } from "@portal/app/_components/ui-elements/LoadingIndicator";
import { detectKnownClientError } from "@portal/app/_common/lib/clientErrorUtils";
import { LoginWithAppleForSignInDocument } from "./generated";
import {
  NO_SHARED_LOGINS_ERROR,
  SIGN_IN_METHOD,
  useSignInContext,
} from "@portal/app/_components/flow/steps/SignInStep";
import { useFlowContext } from "@portal/app/_components/flow/FlowContext";
import { useApolloCache } from "@portal/app/_components/apolloUtils";
import { AppleAuth } from "@portal/app/login/_components/AppleAuth";
import { SignInType } from "@portal/app/login/SignInFlowController";
import { useSignUpAnalytics } from "@portal/app/_components/hooks/useSignUpAnalytics";
import { LOCAL_STORAGE_KEYS, useLocalStorage } from "@portal/app/_components/hooks/useLocalStorage";
import { AppleAuthResponse } from "react-apple-signin-auth";

void gql`
  mutation loginWithAppleForSignIn(
    $appleToken: String!
    $affiliateSlug: String
    $affiliateExternalId: String
    $affiliateFirstEncounteredAt: String
  ) {
    loginWithApple(
      appleToken: $appleToken
      affiliateSlug: $affiliateSlug
      affiliateExternalId: $affiliateExternalId
      affiliateFirstEncounteredAt: $affiliateFirstEncounteredAt
    ) {
      id
      tokens {
        accessToken
        refreshToken
      }
      customer {
        id
        signInFlow {
          ...SignInFlowFragment
        }
      }
    }
  }
`;

export const AppleAuthForSignIn = ({
  propsToContainer,
  signInType,
}: {
  propsToContainer?: React.ComponentProps<typeof Box>;
  signInType: SignInType;
}) => {
  const { setClientErrorMessage } = useSignInContext();
  const [loginWithAppleForSignIn] = useMutation(LoginWithAppleForSignInDocument);
  const [errors, setErrors] = useState<{
    source: string;
    response: string;
  } | null>();
  const [appleDetails, setAppleDetails] = useState<AppleAuthResponse["user"] | null>(null);
  const [, setMethod, removeMethod] = useLocalStorage<string>(LOCAL_STORAGE_KEYS.MAGIC_LINK_METHOD);
  const { evictRootQuery } = useApolloCache();
  const getSignUpAnalytics = useSignUpAnalytics();
  const { getFlowArgs } = useFlowContext();

  const onCredentialResponse = useCallback(
    (response: AppleAuthResponse) => {
      const idToken = response?.authorization?.id_token;
      setAppleDetails(response?.user || null);
      try {
        setMethod(SIGN_IN_METHOD.APPLE);
        return loginWithAppleForSignIn({
          variables: {
            appleToken: idToken,
            ...getSignUpAnalytics(),
            ...getFlowArgs(),
          },
        })
          .then(() => {
            evictRootQuery("signInFlow");
          })
          .catch((error) => {
            setAppleDetails(null);
            if (detectKnownClientError(error) === ClientFacingErrors.UserIsSharedGoogleAccount) {
              setClientErrorMessage(NO_SHARED_LOGINS_ERROR);
            } else {
              setClientErrorMessage({
                title: "Something went wrong",
                description: "Please try again",
              });
            }
            removeMethod();
          });
      } catch (err) {
        setErrors({ source: "Dreambound", response: err.message });
      }
    },
    [
      loginWithAppleForSignIn,
      evictRootQuery,
      getFlowArgs,
      getSignUpAnalytics,
      removeMethod,
      setClientErrorMessage,
      setMethod,
    ]
  );

  return appleDetails ? (
    <LoadingIndicator
      fullScreen
      title={`Hey, ${appleDetails?.name?.firstName || "there"}!`}
      content="Signing you in..."
    />
  ) : errors ? (
    <Alert color="error" icon={<FontAwesomeIcon icon={faTriangleExclamation} />}>
      <AlertTitle>Apple sign-in isn&apos;t working</AlertTitle>
      <Typography>Please use another sign-in method. Sorry!</Typography>
    </Alert>
  ) : (
    <AppleAuth
      onSuccess={onCredentialResponse}
      onError={() => {
        setErrors({
          source: "Dreambound",
          response: "Apple sign-in isn't working",
        });
      }}
      propsToContainer={propsToContainer}
      signInType={signInType}
    />
  );
};
